import { useEffect, useState } from "react";
import {
    Layout,
    Table,
    Input,
    Button,
    Space,
    Tag,
    Card,
    Form,
    Select,
    Breadcrumb,
    Typography,
    message,
    Row,
    Col,
    Spin,
    Popconfirm,
    Modal,
    InputNumber,
    DatePicker,
    Checkbox,
    Switch,
    Tooltip
} from "antd";
import { APPBACKGROUND_COLOR } from "../appsettings";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import { Content } from "antd/es/layout/layout";
import { Link } from "react-router-dom";
import { SearchOutlined, EyeOutlined, EditOutlined, ClearOutlined, FilterOutlined, DeleteOutlined, PlusOutlined, PrinterOutlined, UserAddOutlined } from "@ant-design/icons";
import axiosClient from "../../api/axiosClient";
import { PDFViewer } from "@react-pdf/renderer";
import MemberReportPDF from "./member_report-pdf";
const { Text } = Typography;

export const MembersView = () => {
    const [loading, setLoading] = useState(false);
    const [printingItemId, setPrintingItemId] = useState(null);
    const [members, setMembers] = useState([]);

    const [filters, setFilters] = useState({ search: "", status: "all", gender: "all" });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [form] = Form.useForm();
    const [metricsForm] = Form.useForm();
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [selectedMemberForMetrics, setSelectedMemberForMetrics] = useState(null);
    const USERID = localStorage.getItem("USERID");
    const [printPreview, setPrintPreview] = useState(false);
    const [selectedMemberForPrint, setSelectedMemberForPrint] = useState(null);

    // Print Logic States
    const [includeMetrics, setIncludeMetrics] = useState(true);
    const [includeAssignments, setIncludeAssignments] = useState(true);
    const [assignedTrainersForPrint, setAssignedTrainersForPrint] = useState([]);
    const [accessLoading, setAccessLoading] = useState(null); // tracks which member_id is loading


    const fetchMembers = async (page = 1, pageSize = 10, search = "", status = "", gender = "") => {
        try {
            setLoading(true);

            const response = await axiosClient.get(
                `/member/get_all_members.php`,
                {
                    params: {
                        page: page,
                        pageSize: pageSize,
                        search: search,
                        status: status,
                        gender: gender
                    }
                }
            );

            const result = response.data;
            setMembers(result.data || []);
            setPagination({ current: page, pageSize, total: result.total || 0 });
        } catch (err) {
            console.error("Failed to fetch members", err);
            message.error(err.response?.data?.message || "Failed to fetch members");
        } finally {
            setLoading(false);
        }
    };


    const handlePrintPreview = async (id) => {
        try {
            setPrintingItemId(id);
            const response = await axiosClient.get(
                `/member/get_member_by_id_with_metrics.php?id=${id}`
            );
            const result = response.data;
            setSelectedMemberForPrint(result.data || []);

            // Fetch Assigned Trainers
            try {
                const trainerResponse = await axiosClient.get(`/member/get_assigned_trainers.php?member_id=${id}`);
                if (trainerResponse.data.status === "success") {
                    setAssignedTrainersForPrint(trainerResponse.data.data || []);
                } else {
                    setAssignedTrainersForPrint([]);
                }
            } catch (error) {
                console.error("Error fetching trainers for print:", error);
                setAssignedTrainersForPrint([]);
            }

            setPrintPreview(true);
        } catch (err) {
            console.error("Failed to fetch member for print", err);
            message.error(err.response?.data?.message || "Failed to fetch member for print");
        } finally {
            setPrintingItemId(null);
        }
    }

    const deleteMember = async (id) => {
        try {
            setLoading(true);
            const response = await axiosClient.post(
                `/member/delete_member_by_id.php?id=${id}`,
                {
                    id: id,
                    by: USERID
                }
            );
            const data = response.data;
            if (data.status === "success") {
                message.success(data.message);
                fetchMembers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
            } else {
                message.error(data.message);
            }
        } catch (error) {
            console.error("Delete error:", error);
            message.error(error.response?.data?.message || "Failed to delete member");
        } finally {
            setLoading(false);
        }
    }

    const openMetricsModal = (member) => {
        setSelectedMemberForMetrics(member);
        setIsMetricsModalOpen(true);
        metricsForm.resetFields();
    };

    const handleMetricsSubmit = async (values) => {
        try {
            setLoading(true);
            const payload = {
                ...values,
                date: values.date ? values.date.format("YYYY-MM-DD HH:mm:ss") : null,
                member_id: Number.parseInt(selectedMemberForMetrics.id),
                created_by: USERID
            };

            const response = await axiosClient.post(
                `/member/metric/add_member_metric.php`,
                payload
            );

            const data = response.data;
            if (data.status === "success") {
                message.success("Metrics added successfully");
                setIsMetricsModalOpen(false);
                metricsForm.resetFields();
            } else {
                message.error(data.message || "Failed to add metrics");
            }
        } catch (error) {
            console.error("Metrics error:", error);
            message.error(error.response?.data?.message || "Failed to add metrics");
        } finally {
            setLoading(false);
        }
    };

    const calculateBMI = (changedValues, allValues) => {
        const { weight, height } = allValues;
        if (weight && height) {
            const heightInMeters = height / 100;
            const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(2);
            metricsForm.setFieldsValue({ bmi: bmi });
        }
    };

    // ========== Portal Access Management ==========
    const grantPortalAccess = async (memberId) => {
        try {
            setAccessLoading(memberId);
            const response = await axiosClient.post('/member/member_access_manage.php', {
                action: 'create',
                member_id: memberId
            });
            if (response.data.status === 'success') {
                message.success(response.data.message);
                fetchMembers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            console.error('Grant access error:', error);
            message.error(error.response?.data?.message || 'Failed to grant portal access');
        } finally {
            setAccessLoading(null);
        }
    };

    const togglePortalAccess = async (memberId, newActiveState) => {
        try {
            setAccessLoading(memberId);
            const response = await axiosClient.post('/member/member_access_manage.php', {
                action: 'toggle',
                member_id: memberId,
                is_active: newActiveState ? 1 : 0
            });
            if (response.data.status === 'success') {
                message.success(response.data.message);
                fetchMembers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
            } else {
                message.error(response.data.message);
            }
        } catch (error) {
            console.error('Toggle access error:', error);
            message.error(error.response?.data?.message || 'Failed to toggle portal access');
        } finally {
            setAccessLoading(null);
        }
    };

    useEffect(() => {
        const delay = setTimeout(() => {
            fetchMembers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
        }, 300);
        return () => clearTimeout(delay);
    }, [pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender]);

    const columns = [
        {
            title: "Member ID",
            dataIndex: "member_id",
            key: "member_id",
            width: 100,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: "NIC",
            dataIndex: "nic",
            key: "nic",
            responsive: ['md'],
        },
        {
            title: "Birthday",
            dataIndex: "birthday",
            key: "birthday",
            render: (text) => text || "N/A",
            responsive: ['lg'],
        },
        {
            title: "Gender",
            dataIndex: "gender",
            key: "gender",
            render: (text) => (text === "M" ? "Male" : text === "F" ? "Female" : "N/A"),
            responsive: ['lg'],
        },
        {
            title: "Phone",
            dataIndex: "contact_no",
            key: "contact_no",
            render: (text) => text || "N/A",
            responsive: ['sm'],
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => (
                <Tag color={status ? "success" : "error"}>
                    {status ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, r) => (
                <Space>
                    <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrintPreview(r.id)} loading={printingItemId === r.id} />
                    <Link to={`/members/view/${r.id}`}>
                        <Button
                            type="primary"
                            icon={<EyeOutlined />} size="small" />
                    </Link>
                    <Link to={`/members/edit/${r.id}`}>
                        <Button type="primary"
                            icon={<EditOutlined />}
                            size="small"
                            style={{
                                backgroundColor: "#FFC107",
                                borderColor: "#FFC107",
                                color: "white",
                            }} />
                    </Link>
                    <Button
                        icon={<PlusOutlined />}
                        size="small"
                        type="primary"
                        style={{
                            backgroundColor: "#d200eeff",
                            borderColor: "#d200eeff",
                            color: "white",
                        }}
                        onClick={() => openMetricsModal(r)}
                        title="Add Metrics"
                    />
                    <Popconfirm
                        title="Are you sure to delete this member?"
                        onConfirm={() => deleteMember(r.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            type="primary"
                            danger
                        />
                    </Popconfirm>
                </Space>
            ),
        },
        {
            title: "Portal Access",
            key: "portal_access",
            width: 160,
            align: "center",
            render: (_, r) => {
                if (!r.has_portal_access) {
                    return (
                        <Popconfirm
                            title="Grant portal access to this member?"
                            description="The member will need to use 'Forgot Password' to set their password."
                            onConfirm={() => grantPortalAccess(r.id)}
                            okText="Yes, Grant"
                            cancelText="Cancel"
                        >
                            <Button
                                icon={<UserAddOutlined />}
                                size="small"
                                type="primary"
                                style={{
                                    backgroundColor: "#722ed1",
                                    borderColor: "#722ed1",
                                }}
                                loading={accessLoading === r.id}
                            >
                                Grant Access
                            </Button>
                        </Popconfirm>
                    );
                }
                return (
                    <Space direction="vertical" size={4} style={{ width: '100%', alignItems: 'center' }}>
                        <Tooltip title={r.portal_is_active ? "Click to disable access" : "Click to enable access"}>
                            <Switch
                                checked={r.portal_is_active}
                                onChange={(checked) => togglePortalAccess(r.id, checked)}
                                loading={accessLoading === r.id}
                                checkedChildren="Active"
                                unCheckedChildren="Disabled"
                                style={{
                                    backgroundColor: r.portal_is_active ? "#52c41a" : "#ff4d4f",
                                }}
                            />
                        </Tooltip>
                        {r.portal_need_reset && (
                            <Tag color="warning" style={{ fontSize: '10px', margin: 0, padding: '0 4px' }}>
                                Setup Required
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <Spin spinning={loading}>
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>
                <Headerdiv />

                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item>
                            <Link to={"/members"}>Members</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Text>Member List</Text>
                        </Breadcrumb.Item>
                    </Breadcrumb>

                    {/* Filters */}
                    <Card
                        style={{ marginBottom: 10 }}
                        size="small"
                        title="Filter Members"
                        extra={
                            <Link to="/members/create">
                                <Button icon={<PlusOutlined />} type="primary">Add Member</Button>
                            </Link>
                        }
                    >
                        <Form form={form} layout="vertical">
                            <Row gutter={[16, 0]}>
                                {/* Search Input */}
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item name="search" label="Search">
                                        <Input
                                            placeholder="Name, NIC or ID"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) =>
                                                setFilters((prev) => ({ ...prev, search: e.target.value }))
                                            }
                                        />
                                    </Form.Item>
                                </Col>

                                {/* Status Filter */}
                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="status" label="Status" initialValue="all">
                                        <Select
                                            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                                            options={[
                                                { label: "All Status", value: "all" },
                                                { label: "Active", value: "active" },
                                                { label: "Inactive", value: "inactive" },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* NEW: Gender Filter */}
                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="gender" label="Gender" initialValue="all">
                                        <Select
                                            onChange={(value) => setFilters((prev) => ({ ...prev, gender: value }))}
                                            options={[
                                                { label: "All Genders", value: "all" },
                                                { label: "Male", value: "M" },
                                                { label: "Female", value: "F" },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* Buttons */}
                                <Col xs={24} sm={24} md={8} style={{ display: 'flex', alignItems: 'center' }}>
                                    <Space style={{ marginTop: 5 }}>
                                        <Button
                                            icon={<ClearOutlined />}
                                            onClick={() => {
                                                form.resetFields();
                                                setFilters({ search: "", status: "all", gender: "all" });
                                            }}
                                        >
                                            Reset
                                        </Button>
                                        <Button icon={<FilterOutlined />} type="primary" onClick={() => fetchMembers(1, pagination.pageSize, filters.search, filters.status, filters.gender)}>
                                            Refresh
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    {/* Members Table */}
                    <Card title="Members List" size="small">
                        <Table
                            loading={loading}
                            columns={columns}
                            dataSource={members}
                            rowKey="id"
                            bordered
                            size="small"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} members`,
                                onChange: (page, pageSize) =>
                                    setPagination({ ...pagination, current: page, pageSize }),
                            }}
                            scroll={{ x: 1000 }}
                        />
                    </Card>

                    <Modal
                        title={`Add Metrics for ${selectedMemberForMetrics?.name}`}
                        open={isMetricsModalOpen}
                        onCancel={() => setIsMetricsModalOpen(false)}
                        footer={null}
                    >
                        <Form
                            form={metricsForm}
                            layout="vertical"
                            onFinish={handleMetricsSubmit}
                            onValuesChange={calculateBMI}
                        >
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="date"
                                        label="Date & Time"
                                        rules={[{ required: true, message: 'Please select date and time' }]}
                                    >
                                        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="weight"
                                        label="Weight (kg)"
                                        rules={[{ required: true, message: 'Please enter weight' }]}
                                    >
                                        <InputNumber placeholder="65kg" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="height"
                                        label="Height (cm)"
                                        rules={[{ required: true, message: 'Please enter height' }]}
                                    >
                                        <InputNumber placeholder="172cm" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="bmi"
                                        label="BMI"
                                    >
                                        <InputNumber placeholder="Auto Generated" style={{ width: '100%' }} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="body_fat"
                                        label="Body Fat (%)"
                                    >
                                        <InputNumber placeholder="10%" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="muscle_mass"
                                        label="Muscle Mass (kg)"
                                    >
                                        <InputNumber placeholder="20%" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="end">
                                <Space>
                                    <Button onClick={() => setIsMetricsModalOpen(false)}>Cancel</Button>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Save Metrics
                                    </Button>
                                </Space>
                            </Row>
                        </Form>
                    </Modal>

                    <Modal
                        title="Member Print Preview"
                        open={printPreview}
                        onCancel={() => setPrintPreview(false)}
                        footer={null}
                        width={1000}
                        centered
                        destroyOnHidden
                        height={400}
                    >
                        <div style={{ marginBottom: 15, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Typography.Text strong>Print Options:</Typography.Text>
                            <Checkbox
                                checked={includeMetrics}
                                onChange={(e) => setIncludeMetrics(e.target.checked)}
                            >
                                Include Metrics History
                            </Checkbox>
                            <Checkbox
                                checked={includeAssignments}
                                onChange={(e) => setIncludeAssignments(e.target.checked)}
                            >
                                Include Assigned Trainers
                            </Checkbox>
                        </div>
                        {selectedMemberForPrint && (
                            <PDFViewer key={`${includeMetrics}-${includeAssignments}`} width="100%" height="500px" showToolbar={true}>
                                <MemberReportPDF
                                    member={selectedMemberForPrint}
                                    assignedTrainers={assignedTrainersForPrint}
                                    showMetrics={includeMetrics}
                                    showAssignments={includeAssignments}
                                />
                            </PDFViewer>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};